import {
  BarColor,
  Box,
  Colors,
  InfoDot,
  StackedBar,
  Text,
} from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { budgetTypesEnum } from 'modules/project_management/lib/constants'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'
import {
  MoneyWithStringCurrency,
  getFixedCostPlanningProgressBarLabel,
  getFixedTimeBudgetProgressBarLabel,
} from 'modules/project_management/lib/formatText'

export interface IPlanningStackedBarProps {
  planningSelected: budgetTypesEnum
  allocated: number
  planned: number
  currency: string
  locale: string
}

const PlanningStackedBar = ({
  planningSelected,
  allocated,
  planned,
  currency,
  locale,
}: IPlanningStackedBarProps) => {
  const remaining = Math.abs(planned - allocated)
  const isFixedCostPlanning =
    planningSelected === budgetTypesEnum.fixedCostPlanning ||
    planningSelected === budgetTypesEnum.projectFixedCost
  const isWithinBudget = planned >= allocated

  let label
  let allocatedColor: BarColor
  let allocatedDotColor: Colors
  let remainingLabel

  if (isFixedCostPlanning) {
    allocatedColor = ['champagne1200', 'champagne1200']
    allocatedDotColor = 'champagne1200'
    label = getFixedCostPlanningProgressBarLabel(
      Math.round(allocated * 100),
      Math.round(planned * 100),
      currency,
      locale
    )
    remainingLabel = MoneyWithStringCurrency(Math.round(remaining * 100), currency, locale)
  } else {
    allocatedColor = ['viridian1000', 'viridian1000']
    allocatedDotColor = 'viridian1000'
    label = getFixedTimeBudgetProgressBarLabel(
      formatProjectHours(allocated),
      formatProjectHours(planned)
    )
    remainingLabel = formatProjectHours(remaining)
  }

  let remainingColor: BarColor
  let allocatedStack
  let remainingDotColor: Colors
  let dotTag

  if (isWithinBudget) {
    remainingColor = ['grey600', 'grey400']
    allocatedStack = allocated
    remainingDotColor = 'grey600'
    dotTag = i18n.t('project_management.insights.tags.remaining', {
      remaining_allocation: remainingLabel,
    })
  } else {
    remainingColor = ['radicalRed1000', 'radicalRed1000']
    allocatedStack = planned
    remainingDotColor = 'radicalRed1000'
    dotTag = i18n.t('project_management.insights.tags.overspent', {
      overspent_allocation: remainingLabel,
    })
  }

  const dotLegend = (dotColor: Colors, legendLabel: string) => {
    return (
      <Box flexDirection="row" alignItems="center" gap="s6">
        <InfoDot size="small" color={dotColor} />
        <Text.Normal size="100">{legendLabel}</Text.Normal>
      </Box>
    )
  }

  const dataTestIdAllocatedLabel = `allocated ${allocatedColor}`.split(/[0-9]/)[0]
  const dataTestIdRemainingLabel = `remaining ${remainingColor}`.split(/[0-9]/)[0]

  return (
    <Box flexDirection="column" gap="s6">
      <Text.Header2 weight="semibold">{label}</Text.Header2>
      <StackedBar>
        <StackedBar.Bar
          value={allocatedStack}
          color={allocatedColor}
          data-testid={dataTestIdAllocatedLabel}
        />
        <StackedBar.Bar
          value={remaining}
          color={remainingColor}
          data-testid={dataTestIdRemainingLabel}
        />
      </StackedBar>

      <Box flexDirection="row" alignItems="center" gap="s6">
        {dotLegend(allocatedDotColor, i18n.t('project_management.insights.tags.registered'))}
        {dotLegend(remainingDotColor, dotTag)}
      </Box>
    </Box>
  )
}
export default PlanningStackedBar
